import React, { useContext, useRef, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { HighRiskContext } from "../../../context/HighRiskContext";
import {
  CircularProgress,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  TextField,
  Chip,
  Button,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import VisibilityIcon from "@mui/icons-material/Visibility";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import useClinicalLocations from "../../../hooks/useClinicalLocations";
import { useObservationsModal } from "../../../../../context/ObservationsModalContext/ObservationsModalContext";
import { getObservationsColumn } from "../../../../../components/Advanced/DataGrid/observationsColumn";
import { formatDateForTable } from "../../../common/helpers";

// Los 3 toggles ppvEr/lace/bhStatus son estados ternarios en la UI:
// "" = no filtrar (null en API), "true" y "false".
const FLAG_OPTIONS = [
  { value: "", label: "All" },
  { value: "true", label: "Yes" },
  { value: "false", label: "No" },
];

const flagValueToBool = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  return value === "true" || value === true;
};

const boolToFlagValue = (value) => {
  if (value === null || value === undefined) return "";
  return value ? "true" : "false";
};

const HighRiskTable = ({ selectedLocation, setSelectedLocation }) => {
  const { showObservationsModal } = useObservationsModal();
  const { locations: clinicalLocations } = useClinicalLocations();
  const { highRiskPatients, loading, error, filters, updateFilter, resetFilters } =
    useContext(HighRiskContext);
  const navigate = useNavigate();
  const location = useLocation();
  const tableRef = useRef(null);

  const [searchNameDraft, setSearchNameDraft] = useState("");
  const [cipIdDraft, setCipIdDraft] = useState("");

  useEffect(() => {
    const id = setTimeout(() => {
      updateFilter("searchName", searchNameDraft.trim() || null);
    }, 400);
    return () => clearTimeout(id);
  }, [searchNameDraft, updateFilter]);

  useEffect(() => {
    const id = setTimeout(() => {
      updateFilter("cipId", cipIdDraft.trim() || null);
    }, 400);
    return () => clearTimeout(id);
  }, [cipIdDraft, updateFilter]);

  useEffect(() => {
    if (tableRef.current) {
      setTimeout(() => {
        tableRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 200);
    }
  }, [selectedLocation]);

  const handleLocationChange = (value) => {
    setSelectedLocation(value);
    updateFilter("locationId", value === "" ? null : value);
  };

  const handleReset = () => {
    setSearchNameDraft("");
    setCipIdDraft("");
    setSelectedLocation("");
    resetFilters();
  };

  const handleViewPatient = (row) => {
    navigate(`/patient-info/${row.cipId}`, {
      state: {
        patientRow: {
          ...row,
          memberFullName: row.fullName,
          memberId: row.cipId,
          dob: row.dateOfBirth,
        },
        returnPath: location.pathname,
      },
    });
  };

  const columns = [
    getObservationsColumn(showObservationsModal, 5, {
      getCipId: (row) => row.cipId,
      getPatientName: (row) => row.fullName || "",
    }),
    {
      field: "actions",
      headerName: "",
      width: 60,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Tooltip title="View Patient Info">
          <IconButton
            size="small"
            color="primary"
            onClick={(e) => {
              e.stopPropagation();
              handleViewPatient(params.row);
            }}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
    {
      field: "flagsActive",
      headerName: "Flags",
      width: 100,
      renderCell: (params) => {
        const n = params.value ?? 0;
        const color = n >= 3 ? "error" : n === 2 ? "warning" : n === 1 ? "info" : "default";
        return <Chip size="small" label={`${n}/3`} color={color} />;
      },
    },
    { field: "cipId", headerName: "CIP ID", width: 140 },
    { field: "fullName", headerName: "Patient Name", flex: 2, minWidth: 260 },
    {
      field: "dateOfBirth",
      headerName: "DOB",
      width: 120,
      valueGetter: (value) => formatDateForTable(value),
    },
    { field: "gender", headerName: "Gender", width: 80 },
    {
      field: "ppvEr",
      headerName: "PPV-ER",
      width: 100,
      renderCell: (params) =>
        params.value ? <Chip size="small" label="PPV-ER" color="error" /> : "—",
    },
    {
      field: "lace",
      headerName: "LACE",
      width: 90,
      renderCell: (params) =>
        params.value ? <Chip size="small" label="LACE" color="warning" /> : "—",
    },
    {
      field: "bhStatus",
      headerName: "BH",
      width: 80,
      renderCell: (params) =>
        params.value ? <Chip size="small" label="BH" color="secondary" /> : "—",
    },
    { field: "phoneNumber", headerName: "Phone", width: 150 },
    { field: "mobileNumber", headerName: "Mobile", width: 150 },
    { field: "memberEmail", headerName: "Email", flex: 1, minWidth: 200 },
    {
      field: "firstSeen",
      headerName: "First Seen",
      width: 120,
      valueGetter: (value) => formatDateForTable(value),
    },
    {
      field: "lastSeen",
      headerName: "Last Seen",
      width: 120,
      valueGetter: (value) => formatDateForTable(value),
    },
    {
      field: "status",
      headerName: "Active",
      width: 90,
      renderCell: (params) =>
        params.value === 1 ? (
          <Chip size="small" label="Active" color="success" />
        ) : (
          <Chip size="small" label="Inactive" />
        ),
    },
    { field: "medicalCenter", headerName: "Medical Center", flex: 1.2, minWidth: 180 },
    { field: "locationAddress", headerName: "Address", flex: 1.5, minWidth: 280 },
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <Typography color="error">Error fetching data</Typography>
      </Box>
    );
  }

  return (
    <>
      {/* Filtros */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          mb: 3,
          px: 1,
          alignItems: "center",
        }}
      >
        <TextField
          size="small"
          label="Search by name"
          value={searchNameDraft}
          onChange={(e) => setSearchNameDraft(e.target.value)}
          sx={{ minWidth: 200 }}
        />
        <TextField
          size="small"
          label="CIP ID"
          value={cipIdDraft}
          onChange={(e) => setCipIdDraft(e.target.value)}
          sx={{ minWidth: 160 }}
        />
        <TextField
          size="small"
          type="date"
          label="DOB"
          InputLabelProps={{ shrink: true }}
          value={filters.dateOfBirth || ""}
          onChange={(e) => updateFilter("dateOfBirth", e.target.value || null)}
          sx={{ minWidth: 160 }}
        />
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="high-risk-location-label">Location</InputLabel>
          <Select
            labelId="high-risk-location-label"
            value={selectedLocation}
            label="Location"
            onChange={(e) => handleLocationChange(e.target.value)}
          >
            <MenuItem value="">All Locations</MenuItem>
            {clinicalLocations.map((loc) => (
              <MenuItem key={loc.id} value={loc.id}>
                {loc.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel id="high-risk-ppver-label">PPV-ER</InputLabel>
          <Select
            labelId="high-risk-ppver-label"
            value={boolToFlagValue(filters.ppvEr)}
            label="PPV-ER"
            onChange={(e) => updateFilter("ppvEr", flagValueToBool(e.target.value))}
          >
            {FLAG_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel id="high-risk-lace-label">LACE</InputLabel>
          <Select
            labelId="high-risk-lace-label"
            value={boolToFlagValue(filters.lace)}
            label="LACE"
            onChange={(e) => updateFilter("lace", flagValueToBool(e.target.value))}
          >
            {FLAG_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel id="high-risk-bh-label">BH Status</InputLabel>
          <Select
            labelId="high-risk-bh-label"
            value={boolToFlagValue(filters.bhStatus)}
            label="BH Status"
            onChange={(e) => updateFilter("bhStatus", flagValueToBool(e.target.value))}
          >
            {FLAG_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControlLabel
          control={
            <Switch
              checked={!filters.onlyActive}
              onChange={(e) => updateFilter("onlyActive", !e.target.checked)}
            />
          }
          label="Include inactive"
        />
        <Button
          variant="outlined"
          size="small"
          startIcon={<RestartAltIcon />}
          onClick={handleReset}
        >
          Reset
        </Button>
      </Box>

      {/* Tabla */}
      <Box ref={tableRef} sx={{ height: 560, width: "100%" }}>
        <DataGrid
          getRowId={(row) => row.highRiskId}
          rows={highRiskPatients}
          columns={columns}
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: { paginationModel: { pageSize: 10, page: 0 } },
          }}
          disableRowSelectionOnClick
          getRowClassName={(params) =>
            params.row.status !== 1 ? "row-inactive" : ""
          }
          sx={{
            "& .row-inactive": { backgroundColor: "#F4F6F8", color: "text.secondary" },
          }}
        />
      </Box>
    </>
  );
};

export default HighRiskTable;
